import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/Common";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Trash, CheckCircle, DotsThreeVertical, PencilSimple, Eye, Copy, ArrowUUpLeft, XCircle } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const EMPTY_ITEM = { material_id: "", name: "", qty: 0, rate: 0, amount: 0 };

// ============== PO Editor Dialog (create / edit / view / duplicate) ==============
function POEditor({ open, mode, initial, suppliers, materials, onClose, onDone }) {
  const [supId, setSupId] = useState("");
  const [items, setItems] = useState([EMPTY_ITEM]);
  const [remarks, setRemarks] = useState("");
  const [expected, setExpected] = useState("");
  const readOnly = mode === "view";

  useEffect(() => {
    if (open) {
      setSupId(initial?.supplier_id || "");
      setItems(initial?.items?.length ? initial.items : [EMPTY_ITEM]);
      setRemarks(initial?.remarks || "");
      setExpected(initial?.expected_date ? String(initial.expected_date).slice(0, 10) : "");
    }
  }, [open, initial]);

  const upd = (i, field, val) => {
    const c = [...items]; c[i][field] = val;
    if (field === "material_id") {
      const m = materials.find(x => x.id === val);
      if (m) { c[i].name = m.name; c[i].rate = m.rate; }
    }
    c[i].amount = (parseFloat(c[i].qty) || 0) * (parseFloat(c[i].rate) || 0);
    setItems(c);
  };
  const total = items.reduce((a, x) => a + (x.amount || 0), 0);

  const submit = async () => {
    try {
      const sup = suppliers.find(s => s.id === supId);
      const body = { supplier_id: supId, supplier_name: sup?.name, items, remarks,
                      expected_date: expected || null };
      if (mode === "edit") {
        await api.put(`/purchase-orders/${initial.id}`, body);
        toast.success("PO updated");
      } else if (mode === "duplicate") {
        // duplicate uses server-side handler
        await api.post(`/purchase-orders/${initial.id}/duplicate`);
        toast.success("PO duplicated");
      } else {
        await api.post("/purchase-orders", body);
        toast.success("PO created");
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const title = { edit: "Edit Purchase Order", view: "Purchase Order Details",
                    duplicate: `Duplicate ${initial?.po_number || ""}`, create: "Create Purchase Order" }[mode] || "Purchase Order";

  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>

        {mode === "duplicate" ? (
          <div className="py-4">
            <p className="text-sm text-muted-foreground">Create a new draft PO from <b>{initial?.po_number}</b> with the same supplier and line items. You can edit it before receiving.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Supplier</Label>
                <Select value={supId} onValueChange={setSupId} disabled={readOnly}>
                  <SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Select supplier"/></SelectTrigger>
                  <SelectContent>{suppliers.filter(s => s.status !== "deleted").map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Expected Delivery</Label>
                <Input type="date" value={expected} onChange={e => setExpected(e.target.value)} disabled={readOnly}/>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <Label>Items</Label>
                {!readOnly && <Button size="sm" variant="outline" onClick={() => setItems([...items, {...EMPTY_ITEM}])}><Plus size={14}/> Add Item</Button>}
              </div>
              <div className="space-y-2">
                {items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5">
                      <Select value={it.material_id} onValueChange={v => upd(i, "material_id", v)} disabled={readOnly}>
                        <SelectTrigger><SelectValue placeholder="Material"/></SelectTrigger>
                        <SelectContent>{materials.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2"><Input type="number" placeholder="Qty" value={it.qty} onChange={e => upd(i, "qty", e.target.value)} disabled={readOnly}/></div>
                    <div className="col-span-2"><Input type="number" placeholder="Rate" value={it.rate} onChange={e => upd(i, "rate", e.target.value)} disabled={readOnly}/></div>
                    <div className="col-span-2 text-right mono text-sm">₹{(it.amount || 0).toFixed(2)}</div>
                    {!readOnly && <button className="col-span-1 text-red-600 hover:text-red-700" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash size={16}/></button>}
                  </div>
                ))}
              </div>
              <div className="border-t border-border mt-4 pt-3 flex justify-between font-semibold">
                <span>Total</span><span className="mono">₹{total.toFixed(2)}</span>
              </div>
              <div className="mt-3">
                <Label>Remarks</Label>
                <Textarea rows={2} value={remarks} onChange={e => setRemarks(e.target.value)} disabled={readOnly}/>
              </div>
            </div>
          </>
        )}

        <DialogFooter>
          {readOnly ? (
            <Button variant="outline" onClick={onClose}>Close</Button>
          ) : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={mode !== "duplicate" && !supId} data-testid="po-save">
                {mode === "edit" ? "Save Changes" : mode === "duplicate" ? "Duplicate PO" : "Create PO"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Cancel PO Dialog ==============
function CancelPoDialog({ open, po, onClose, onDone }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    try {
      await api.post(`/purchase-orders/${po.id}/cancel`, { reason });
      toast.success("PO cancelled");
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>Cancel PO {po?.po_number}</DialogTitle><DialogDescription className="sr-only">Cancel PO</DialogDescription></DialogHeader>
        <div className="py-2">
          <p className="text-sm text-muted-foreground mb-3">This will mark the PO as Cancelled. It will be preserved for audit but no goods will be received.</p>
          <Label>Reason</Label>
          <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Supplier delay, wrong material, duplicate PO…" data-testid="po-cancel-reason"/>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Keep PO</Button>
          <Button variant="destructive" onClick={submit} data-testid="po-cancel-confirm">Confirm Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Supplier Editor Dialog ==============
const EMPTY_SUP = { code: "", name: "", contact: "", phone: "", email: "", address: "", rating: 4.0, lead_time_days: 3 };

function SupplierEditor({ open, mode, initial, onClose, onDone }) {
  const [form, setForm] = useState(EMPTY_SUP);
  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY_SUP, ...initial } : { ...EMPTY_SUP });
  }, [open, initial]);
  const readOnly = mode === "view";
  const submit = async () => {
    try {
      if (mode === "edit") {
        await api.put(`/suppliers/${initial.id}`, form);
        toast.success("Supplier updated");
      } else if (mode === "duplicate") {
        // Duplicate = create new with same fields except code
        await api.post("/suppliers", { ...form, id: undefined, status: "active" });
        toast.success("Supplier duplicated");
      } else {
        await api.post("/suppliers", form);
        toast.success("Supplier created");
      }
      onDone?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const title = { edit: "Edit Supplier", view: "Supplier Details",
                    duplicate: "Duplicate Supplier", create: "Add Supplier" }[mode] || "Supplier";
  return (
    <Dialog open={open} onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription className="sr-only">{title}</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Code</Label><Input value={form.code} onChange={e => setForm({...form, code: e.target.value})} disabled={readOnly} data-testid="sup-code"/></div>
          <div><Label>Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} disabled={readOnly} data-testid="sup-name"/></div>
          <div><Label>Contact Person</Label><Input value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} disabled={readOnly}/></div>
          <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} disabled={readOnly}/></div>
          <div className="col-span-2"><Label>Email</Label><Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} disabled={readOnly}/></div>
          <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={e => setForm({...form, address: e.target.value})} disabled={readOnly}/></div>
          <div><Label>Rating (0–5)</Label><Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => setForm({...form, rating: parseFloat(e.target.value) || 0})} disabled={readOnly}/></div>
          <div><Label>Lead Time (days)</Label><Input type="number" value={form.lead_time_days} onChange={e => setForm({...form, lead_time_days: parseInt(e.target.value) || 0})} disabled={readOnly}/></div>
        </div>
        <DialogFooter>
          {readOnly ? <Button variant="outline" onClick={onClose}>Close</Button> : (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={!form.code || !form.name} data-testid="sup-save">
                {mode === "edit" ? "Save Changes" : mode === "duplicate" ? "Duplicate" : "Create Supplier"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== Page ==============
export default function Procurement() {
  const { user } = useAuth();
  const role = user?.role;
  const canManage = ["admin", "purchase"].includes(role);

  const [pos, setPos] = useState([]);
  const [sups, setSups] = useState([]);
  const [mats, setMats] = useState([]);
  const [showInactive, setShowInactive] = useState(false);

  // PO dialogs
  const [poMode, setPoMode] = useState(null);  // create | edit | view | duplicate
  const [poSel, setPoSel] = useState(null);
  const [cancelPo, setCancelPo] = useState(null);

  // Supplier dialogs
  const [supMode, setSupMode] = useState(null);
  const [supSel, setSupSel] = useState(null);

  const load = async () => {
    const params = { include_inactive: showInactive };
    setPos((await api.get("/purchase-orders", { params })).data);
    setSups((await api.get("/suppliers", { params })).data);
    setMats((await api.get("/materials")).data);
  };
  useEffect(() => { load(); }, [showInactive]);  // eslint-disable-line

  const openCreate = () => { setPoSel(null); setPoMode("create"); };
  const receive = async (p) => {
    if (!window.confirm(`Receive PO ${p.po_number}? Stock will be updated.`)) return;
    try { await api.post(`/purchase-orders/${p.id}/receive`); toast.success("Received & inventory updated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const restore = async (p) => {
    try { await api.post(`/purchase-orders/${p.id}/restore`); toast.success("PO restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const supRestore = async (s) => {
    try { await api.post(`/suppliers/${s.id}/restore`); toast.success("Supplier restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const supDelete = async (s) => {
    if (!window.confirm(`Delete supplier ${s.name}? Suppliers referenced by materials or POs will be marked Inactive.`)) return;
    try { const r = await api.delete(`/suppliers/${s.id}`); toast.success(r.data.message || "Supplier removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div data-testid="procurement-page">
      <PageHeader subtitle="Purchase & Suppliers" title="Procurement"
        actions={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs font-semibold cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} data-testid="proc-show-inactive"/>
              Show inactive
            </label>
            {canManage && (
              <Button onClick={openCreate} data-testid="new-po-btn">
                <Plus size={16} className="mr-1.5"/>New Purchase Order
              </Button>
            )}
          </div>
        }
      />

      <Tabs defaultValue="orders">
        <TabsList>
          <TabsTrigger value="orders" data-testid="tab-orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="tab-suppliers">Suppliers</TabsTrigger>
        </TabsList>

        {/* ---------- Orders ---------- */}
        <TabsContent value="orders">
          <Card className="shadow-none">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>PO #</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Items</TableHead>
                    <TableHead className="text-right">Total ₹</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pos.map(p => {
                    const isCancelled = p.status === "Cancelled";
                    const isReceived = p.status === "Received";
                    const isDeleted = !!p.deleted_at;
                    return (
                      <TableRow key={p.id} className={isCancelled || isDeleted ? "opacity-60" : ""} data-testid={`po-row-${p.po_number}`}>
                        <TableCell className="mono font-semibold">{p.po_number}</TableCell>
                        <TableCell>{p.supplier_name}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{p.items?.length || 0} items</TableCell>
                        <TableCell className="text-right mono">{p.total?.toLocaleString("en-IN")}</TableCell>
                        <TableCell><StatusBadge status={p.status}/></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{new Date(p.order_date).toLocaleDateString()}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end items-center gap-1">
                            {p.status === "Pending" && canManage && (
                              <Button size="sm" variant="outline" onClick={() => receive(p)} data-testid={`receive-${p.po_number}`}>
                                <CheckCircle size={14} className="mr-1"/>Receive
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`po-actions-${p.po_number}`}>
                                  <DotsThreeVertical size={16} weight="bold"/>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => { setPoSel(p); setPoMode("view"); }} data-testid={`po-view-${p.po_number}`}>
                                  <Eye size={14} className="mr-2"/>View details
                                </DropdownMenuItem>
                                {canManage && p.status === "Pending" && (
                                  <DropdownMenuItem className="text-emerald-700" onSelect={() => receive(p)} data-testid={`po-receive-${p.po_number}`}>
                                    <CheckCircle size={14} className="mr-2"/>Receive & update stock
                                  </DropdownMenuItem>
                                )}
                                {canManage && p.status === "Pending" && (
                                  <DropdownMenuItem onSelect={() => { setPoSel(p); setPoMode("edit"); }} data-testid={`po-edit-${p.po_number}`}>
                                    <PencilSimple size={14} className="mr-2"/>Edit PO
                                  </DropdownMenuItem>
                                )}
                                {canManage && (
                                  <DropdownMenuItem onSelect={() => { setPoSel(p); setPoMode("duplicate"); }} data-testid={`po-dup-${p.po_number}`}>
                                    <Copy size={14} className="mr-2"/>Duplicate
                                  </DropdownMenuItem>
                                )}
                                {canManage && !isReceived && !isCancelled && (
                                  <>
                                    <DropdownMenuSeparator/>
                                    <DropdownMenuItem className="text-red-600" onSelect={() => setCancelPo(p)} data-testid={`po-cancel-${p.po_number}`}>
                                      <XCircle size={14} className="mr-2"/>Cancel PO
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {canManage && isCancelled && (
                                  <DropdownMenuItem className="text-emerald-700" onSelect={() => restore(p)} data-testid={`po-restore-${p.po_number}`}>
                                    <ArrowUUpLeft size={14} className="mr-2"/>Restore
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- Suppliers ---------- */}
        <TabsContent value="suppliers">
          <div className="flex justify-end mb-3">
            {canManage && (
              <Button size="sm" onClick={() => { setSupSel(null); setSupMode("create"); }} data-testid="new-supplier-btn">
                <Plus size={14} className="mr-1"/>Add Supplier
              </Button>
            )}
          </div>
          <Card className="shadow-none">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead><TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead><TableHead>Phone</TableHead>
                    <TableHead className="text-right">Rating</TableHead>
                    <TableHead className="text-right">Lead Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sups.map(s => {
                    const inactive = s.status && s.status !== "active";
                    return (
                      <TableRow key={s.id} className={inactive ? "opacity-60" : ""} data-testid={`sup-row-${s.code}`}>
                        <TableCell className="mono">{s.code}</TableCell>
                        <TableCell className="font-semibold">{s.name}</TableCell>
                        <TableCell>{s.contact || "—"}</TableCell>
                        <TableCell className="mono text-xs">{s.phone || "—"}</TableCell>
                        <TableCell className="text-right"><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">★ {s.rating}</Badge></TableCell>
                        <TableCell className="text-right mono">{s.lead_time_days}d</TableCell>
                        <TableCell>{inactive ? <Badge variant="secondary" className="uppercase text-[9px]">{s.status}</Badge> : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`sup-actions-${s.code}`}>
                                <DotsThreeVertical size={16} weight="bold"/>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onSelect={() => { setSupSel(s); setSupMode("view"); }}>
                                <Eye size={14} className="mr-2"/>View
                              </DropdownMenuItem>
                              {canManage && (
                                <>
                                  <DropdownMenuItem onSelect={() => { setSupSel(s); setSupMode("edit"); }} data-testid={`sup-edit-${s.code}`}>
                                    <PencilSimple size={14} className="mr-2"/>Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => { setSupSel({...s, code: "", id: undefined}); setSupMode("duplicate"); }}>
                                    <Copy size={14} className="mr-2"/>Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator/>
                                  {!inactive ? (
                                    <DropdownMenuItem className="text-red-600" onSelect={() => supDelete(s)} data-testid={`sup-delete-${s.code}`}>
                                      <Trash size={14} className="mr-2"/>Delete
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem className="text-emerald-700" onSelect={() => supRestore(s)} data-testid={`sup-restore-${s.code}`}>
                                      <ArrowUUpLeft size={14} className="mr-2"/>Restore
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <POEditor
        open={!!poMode}
        mode={poMode}
        initial={poSel}
        suppliers={sups}
        materials={mats}
        onClose={() => { setPoMode(null); setPoSel(null); }}
        onDone={load}
      />
      <CancelPoDialog
        open={!!cancelPo}
        po={cancelPo}
        onClose={() => setCancelPo(null)}
        onDone={load}
      />
      <SupplierEditor
        open={!!supMode}
        mode={supMode}
        initial={supSel}
        onClose={() => { setSupMode(null); setSupSel(null); }}
        onDone={load}
      />
    </div>
  );
}
